/**
 * Machine Office — Designsystem.
 *
 * Wer dieses Paket importiert, bekommt das gesamte Regelwerk als
 * Seiteneffekt: erst die Schriften (ein `@import` muss ganz vorn stehen),
 * dann die Tokens, dann die Bausteine.
 */
import "./schriften.css";
import "./tokens.css";
import "./basis.css";

/**
 * Die drei Zustände der Darstellung — dieselben drei, die in der
 * Themawahl unten links stehen: System · Hell · Dunkel.
 *
 * `system` heißt: kein `data-theme` am Wurzelelement, die Palette folgt
 * `prefers-color-scheme`. Die beiden anderen setzen es ausdrücklich und
 * überstimmen damit die Systemwahl.
 */
export type Thema = "system" | "light" | "dark";

/** Wo die Wahl liegt. Ein Schlüssel, damit er nicht an zwei Stellen abweicht. */
const SPEICHER_SCHLUESSEL = "machine-office:thema";

/** Nur diese drei gelten. Alles andere ist ein alter oder fremder Eintrag. */
function istThema(wert: unknown): wert is Thema {
  return wert === "system" || wert === "light" || wert === "dark";
}

/**
 * Setzt die Darstellung und merkt sie sich.
 *
 * Bei `system` wird das Attribut ENTFERNT, nicht auf "system" gesetzt:
 * Die Regeln in tokens.css hängen an `:root:not([data-theme="light"])`
 * innerhalb der Media-Abfrage — ein fremder Attributwert würde dort
 * stillschweigend mitgreifen.
 *
 * Der Zugriff auf `localStorage` steht im try/catch: Im privaten Fenster,
 * bei gesperrten Website-Daten oder in einer Vorschau wirft schon das
 * Lesen. Die Darstellung selbst darf daran nicht scheitern — darum wird
 * zuerst gesetzt und erst danach gespeichert.
 */
export function themaSetzen(t: Thema): void {
  const wurzel = document.documentElement;
  if (t === "system") {
    wurzel.removeAttribute("data-theme");
  } else {
    wurzel.setAttribute("data-theme", t);
  }
  try {
    localStorage.setItem(SPEICHER_SCHLUESSEL, t);
  } catch {
    /* Kein Speicher verfügbar — die Wahl gilt dann nur für diese Sitzung. */
  }
}

/**
 * Liest die gemerkte Darstellung. Ohne Eintrag — und immer dann, wenn der
 * Speicher nicht zu erreichen ist — gilt `system`.
 */
export function themaLesen(): Thema {
  try {
    const wert = localStorage.getItem(SPEICHER_SCHLUESSEL);
    if (istThema(wert)) return wert;
  } catch {
    /* Kein Speicher verfügbar. */
  }
  return "system";
}
